import { create } from 'zustand'
import type { WeightLog, Symptom, Note, SymptomCreate, SymptomUpdate, ClinicalEvent, ClinicalEventCreate, ClinicalEventUpdate } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface TimelineItem {
  id: string
  type: 'weight' | 'symptom' | 'note' | 'medication_log' | 'checkup' | 'vaccine' | 'clinical_event'
  date: string
  data: Record<string, unknown>
}

interface TrackingState {
  weightLogs: WeightLog[]
  symptoms: Symptom[]
  notes: Note[]
  clinicalEvents: ClinicalEvent[]
  timeline: TimelineItem[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchWeightLogs: (petId: string) => Promise<void>
  createWeightLog: (petId: string, peso: number, notas?: string) => Promise<void>
  fetchSymptoms: (petId: string) => Promise<void>
  createSymptom: (petId: string, data: SymptomCreate) => Promise<Symptom>
  updateSymptom: (id: string, data: SymptomUpdate) => Promise<Symptom>
  deleteSymptom: (id: string) => Promise<void>
  fetchNotes: (petId: string) => Promise<void>
  createNote: (petId: string, contenido: string) => Promise<Note>
  updateNote: (id: string, contenido: string) => Promise<Note>
  deleteNote: (id: string) => Promise<void>
  fetchClinicalEvents: (petId: string) => Promise<void>
  createClinicalEvent: (petId: string, data: ClinicalEventCreate) => Promise<ClinicalEvent>
  updateClinicalEvent: (id: string, data: ClinicalEventUpdate) => Promise<ClinicalEvent>
  deleteClinicalEvent: (id: string) => Promise<void>
  fetchTimeline: (petId: string) => Promise<void>
  clearError: () => void
  clearAll: () => void
}

export const useTrackingStore = create<TrackingState>((set) => ({
  weightLogs: [],
  symptoms: [],
  notes: [],
  clinicalEvents: [],
  timeline: [],
  isLoading: false,
  error: null,

  // Weight
  fetchWeightLogs: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ weightLogs: WeightLog[] }>(`/api/pets/${petId}/weight`)
      set({ weightLogs: response.weightLogs, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar historial de peso'
      set({ error: message, isLoading: false })
    }
  },

  createWeightLog: async (petId: string, peso: number, notas?: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.post(`/api/pets/${petId}/weight`, { peso, notas })
      // Refresh weight logs
      const response = await api.get<{ weightLogs: WeightLog[] }>(`/api/pets/${petId}/weight`)
      set({ weightLogs: response.weightLogs, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al registrar peso'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Symptoms
  fetchSymptoms: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ symptoms: Symptom[] }>(`/api/pets/${petId}/symptoms`)
      set({ symptoms: response.symptoms, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar síntomas'
      set({ error: message, isLoading: false })
    }
  },

  createSymptom: async (petId: string, data: SymptomCreate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ symptom: Symptom }>(`/api/pets/${petId}/symptoms`, data)
      set((state) => ({
        symptoms: [response.symptom, ...state.symptoms],
        isLoading: false,
      }))
      return response.symptom
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al registrar síntoma'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateSymptom: async (id: string, data: SymptomUpdate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ symptom: Symptom }>(`/api/symptoms/${id}`, data)
      set((state) => ({
        symptoms: state.symptoms.map((s) => (s.id === id ? response.symptom : s)),
        isLoading: false,
      }))
      return response.symptom
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar síntoma'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteSymptom: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/symptoms/${id}`)
      set((state) => ({
        symptoms: state.symptoms.filter((s) => s.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar síntoma'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Notes
  fetchNotes: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ notes: Note[] }>(`/api/pets/${petId}/notes`)
      set({ notes: response.notes, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar notas'
      set({ error: message, isLoading: false })
    }
  },

  createNote: async (petId: string, contenido: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ note: Note }>(`/api/pets/${petId}/notes`, { contenido })
      set((state) => ({
        notes: [response.note, ...state.notes],
        isLoading: false,
      }))
      return response.note
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al crear nota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateNote: async (id: string, contenido: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ note: Note }>(`/api/notes/${id}`, { contenido })
      set((state) => ({
        notes: state.notes.map((n) => (n.id === id ? response.note : n)),
        isLoading: false,
      }))
      return response.note
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar nota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteNote: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/notes/${id}`)
      set((state) => ({
        notes: state.notes.filter((n) => n.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar nota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Clinical Events
  fetchClinicalEvents: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ clinicalEvents: ClinicalEvent[] }>(`/api/pets/${petId}/clinical-events`)
      set({ clinicalEvents: response.clinicalEvents, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar historia clínica'
      set({ error: message, isLoading: false })
    }
  },

  createClinicalEvent: async (petId: string, data: ClinicalEventCreate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ clinicalEvent: ClinicalEvent }>(`/api/pets/${petId}/clinical-events`, data)
      set((state) => ({
        clinicalEvents: [response.clinicalEvent, ...state.clinicalEvents],
        isLoading: false,
      }))
      return response.clinicalEvent
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al registrar evento clínico'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateClinicalEvent: async (id: string, data: ClinicalEventUpdate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ clinicalEvent: ClinicalEvent }>(`/api/clinical-events/${id}`, data)
      set((state) => ({
        clinicalEvents: state.clinicalEvents.map((e) => (e.id === id ? response.clinicalEvent : e)),
        isLoading: false,
      }))
      return response.clinicalEvent
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar evento clínico'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteClinicalEvent: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/clinical-events/${id}`)
      set((state) => ({
        clinicalEvents: state.clinicalEvents.filter((e) => e.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar evento clínico'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Timeline
  fetchTimeline: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ timeline: TimelineItem[] }>(`/api/pets/${petId}/timeline`)
      set({ timeline: response.timeline, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar historial'
      set({ error: message, isLoading: false })
    }
  },

  clearError: () => set({ error: null }),
  clearAll: () => set({ weightLogs: [], symptoms: [], notes: [], clinicalEvents: [], timeline: [] }),
}))
